import { useSignal } from "@preact/signals-react";
import { ErrorField } from "../../../../types/ErrorField";
import TextEdit from "../../../molecules/TextEdit";
import SelectTextField, { OptionProps } from "../../../molecules/SelectTextField";
import { getErrorMessage } from "../../../../utils/formErrorUtils";
import { useEffect } from "react";
import MaskEdit from "../../../organisms/MaskEdit";
import streetTypesData from "../../../../resources/street_types.json";



interface Props {
    form: any;
    errorFields: ErrorField[];
    changeForm: (field: string, value: string) => void;
    provinces: any[];
    cities: any[],
    changeProvince: (province: string) => void;
    editForm?: boolean;
}


const IndividualFormBasicData: React.FC<Props> = ({ form, errorFields, changeForm, provinces, cities, changeProvince, editForm = false }) => {
    const provincesOpt = useSignal<OptionProps[]>([]);
    const citiesOpt = useSignal<OptionProps[]>([]);
    const isLoadingCities = useSignal(false);
    const streetTypeOptions = useSignal<OptionProps[]>([]);
    const currentIbge = useSignal<string>(""); // ✅ Armazenar IBGE localmente

    // ✅ Função para autofill por CEP (igual ao LegalPersonFormTab1)
    // Função para formatar telefone para a máscara
    const formatPhoneForMask = (phone: string, isNational: boolean = true) => {
        if (!phone) return '';
        
        // Remove tudo que não é dígito
        const digits = phone.replace(/\D/g, '');
        
        if (!isNational) return digits; // Para internacional, retorna apenas dígitos
        
        // Para nacional, formatar conforme a máscara
        if (digits.length === 11) {
            // Celular: (XX)XXXXX-XXXX
            return `(${digits.slice(0, 2)})${digits.slice(2, 7)}-${digits.slice(7)}`;
        } else if (digits.length === 10) {
            // Fixo: (XX)XXXX-XXXX
            return `(${digits.slice(0, 2)})${digits.slice(2, 6)}-${digits.slice(6)}`;
        }
        
        return digits; // Retorna apenas dígitos se não bater com formato esperado
    };

    const handleZipcodeChange = async (value: string) => {
        // Remove formatação e mantém apenas números
        value = value.replace(/[^0-9]/g, "");

        if (value.length === 0) {
            // Limpar campos quando CEP é removido
            changeForm("activeAddress.street_type", "");
            changeForm("activeAddress.street", "");
            changeForm("activeAddress.district", "");
            changeForm("activeAddress.complement", "");
            changeForm("activeAddress.province_initials", "");
            return;
        }

        if (value.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${value}/json/`);
                const data = await response.json();

                if (data.erro) {
                    console.warn("CEP não encontrado:", value);
                    return;
                }

                // ✅ Autofill dos campos de endereço
                const logradouro = data.logradouro || "";
                const streetTypeName = logradouro.split(" ")[0] || "";
                
                // ✅ Buscar streetType correspondente no resource
                const matchingStreetType = streetTypesData.streetTypes.find(st => 
                    st.name === streetTypeName || st.abbreviations.includes(streetTypeName)
                );
                
                // ✅ Remover o tipo de logradouro do campo street
                const streetWithoutType = matchingStreetType 
                    ? logradouro.replace(streetTypeName, "").trim()
                    : logradouro;
                
                changeForm("activeAddress.street_type", matchingStreetType?.name || streetTypeName);
                changeForm("activeAddress.street", streetWithoutType);
                changeForm("activeAddress.district", data.bairro || ""); // ✅ Campo correto
                changeForm("activeAddress.complement", data.complemento || "");
                
                // ✅ Atualizar estado e carregar cidades
                if (data.uf) {
                    changeForm("activeAddress.province_initials", data.uf);
                    changeProvince(data.uf); // Carregar cidades do estado
                }

                // ✅ Setar código IBGE e aguardar cidades
                if (data.ibge) {
                    currentIbge.value = data.ibge;
                    
                    // Aguardar cidades carregarem e selecionar por IBGE
                    setTimeout(() => {
                        const cityMatch = citiesOpt.value.find((city: any) => city.ibgeCode === data.ibge);
                        if (cityMatch) {
                            changeForm("activeAddress.city_id", cityMatch.value);
                            console.log("✅ Cidade selecionada automaticamente:", cityMatch.label);
                        }
                    }, 500); // ✅ Tempo maior para aguardar carregamento
                }

                console.log("✅ Autofill por CEP realizado:", data);
            } catch (error) {
                console.error("❌ Erro ao buscar CEP:", error);
            }
        }
    };

    useEffect(() => {
        // ✅ Popular streetTypeOptions com resource JSON
        streetTypeOptions.value = streetTypesData.streetTypes.map(st => ({
            value: st.name,
            label: st.name
        }));
    }, []);

    useEffect(() => {
        isLoadingCities.value = true;
        if (cities && cities.length > 0) {
            citiesOpt.value = cities.map((city: any) => ({
                label: city.name,
                value: city.id,
                ibgeCode: city.ibgeCode // ✅ Incluir IBGE code para matching
            }));
            if (currentIbge.value) {
                setTimeout(() => {
                    const cityMatch = citiesOpt.value.find((city: any) => city.ibgeCode === currentIbge.value);
                    if (cityMatch) {
                        changeForm("cityId", cityMatch.value);
                        console.log("✅ Cidade selecionada automaticamente:", cityMatch.label);
                    }
                }, 300);
            }
        } else {
            citiesOpt.value = []; // ✅ Garantir array vazio se cities não existir
        }
        isLoadingCities.value = false;
    }, [cities]);
    useEffect(() => {
        if (provinces && provinces.length > 0) {
            provincesOpt.value = provinces.map((province: any) => ({
                label: province.name,
                value: province.initials,
            }));
        } else {
            provincesOpt.value = []; // ✅ Garantir array vazio se provinces não existir
        }

    }, [provinces]);

    return (
        <div className="p-4 m-4">
            {/* Primeira linha: CPF + Nome + Email */}
            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-2">
                    <MaskEdit
                        id="socialId"
                        label="CPF"
                        mask="___.___.___-__"
                        replacement={{ '_': /\d/ }}
                        value={form.socialId}
                        disabled={true}
                        onChange={(e) => changeForm("socialId", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "social_id")}
                    />
                </div>
                <div className="col-span-5">
                    <TextEdit
                        id="name"
                        label="Nome"
                        value={form.name}
                        onChange={(e) => changeForm("name", e.target.value.toUpperCase())}
                        errorMessage={getErrorMessage(errorFields, "name")}
                    />
                </div>
                <div className="col-span-5">
                    <TextEdit
                        id="email"
                        label="Email"
                        value={form.email}
                        onChange={(e) => changeForm("email", e.target.value.toUpperCase())}
                        errorMessage={getErrorMessage(errorFields, "email")}
                    />
                </div>
            </div>

            {/* Segunda linha: Tipo ID + Número + UF emissor + Órgão emissor */}
            <div className="grid grid-cols-12 gap-4 mt-6">
                <div className="col-span-1">
                    <SelectTextField
                        id="idCardType"
                        name='idCardType'
                        label=""
                        disabled={form.blockEdit.blockIdCard}
                        value={form.idCardType}
                        items={[{ "value": "RG", "label": "RG" }, { "value": "RNE", "label": "RNE" }]}
                        onChange={(e) => changeForm("idCardType", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "idCardType")}
                    />
                </div>
                <div className="col-span-3">
                    <TextEdit
                        id="idCard"
                        label="Número RG/RNE"
                        value={form.idCard}
                        disabled={form.blockEdit.blockIdCard}
                        onChange={(e) => changeForm("idCard", e.target.value.toUpperCase())}
                        errorMessage={getErrorMessage(errorFields, "idCardNumber")}
                    />
                </div>
                <div className="col-span-1">
                    <SelectTextField
                        id="idCardProvince"
                        name='idCardProvince'
                        label="UF emissor"
                        value={form.idCardType === "RG" ? form.idCardProvince : ""}
                        items={provincesOpt.value}
                        disabled={form.idCardType === "RNE" || form.blockEdit.blockIdCard}
                        onChange={(e) => changeForm("idCardProvince", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "idCardProvince")}
                    />
                </div>
                <div className="col-span-2">
                    <TextEdit
                        id="issuingAuthority"
                        label="Orgão emissor"
                        value={form.idCardType === "RG" ? form.issuingAuthority : ""}
                        disabled={form.idCardType === "RNE" || form.blockEdit.blockIdCard}
                        onChange={(e) => changeForm("issuingAuthority", e.target.value.toUpperCase())}
                        errorMessage={getErrorMessage(errorFields, "issuingAuthority")}
                    />
                </div>
            </div>

            {/* Terceira linha: Origem celular + Celular + Fixo */}
            <div className="grid grid-cols-12 gap-4 mt-6">
                <div className="col-span-2">
                    <SelectTextField
                        id="isNationalNumber"
                        name='isNationalNumber'
                        label="Origem celular"
                        value={form.isNationalNumber}
                        items={[{ "value": "true", "label": "Brasil" }, { "value": "false", "label": "Estrangeiro" }]}
                        onChange={(e) => changeForm("isNationalNumber", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "isNationalNumber")}
                    />
                </div>
                <div className="col-span-2">
                    <MaskEdit
                        id="mobile"
                        label="Celular"
                        mask={form.isNationalNumber === "true" ? "(__)_____-____" : "___________________________"}
                        replacement={{ '_': /\d/ }}
                        value={formatPhoneForMask(form.mobile, form.isNationalNumber === "true")}
                        onChange={(e) => changeForm("mobile", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "mobile")}
                    />
                </div>
                <div className="col-span-2">
                    <MaskEdit
                        id="phone"
                        label="Fixo"
                        mask="(__)____-____"
                        replacement={{ '_': /\d/ }}
                        value={formatPhoneForMask(form.phone, true)}
                        onChange={(e) => changeForm("phone", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "phone")}
                    />
                </div>
            </div>

            {/* Quarta linha: CEP + Tipo + Logradouro + Número + Complemento */}
            <div className="grid grid-cols-12 gap-4 mt-6">
                <div className="col-span-2">
                    <MaskEdit
                        id="zipcode"
                        label="CEP"
                        mask="_____-___"
                        replacement={{ '_': /\d/ }}
                        value={form.activeAddress?.zipcode === "None" || form.activeAddress?.zipcode === null ? '' : form.activeAddress?.zipcode || ''}
                        onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            changeForm("activeAddress.zipcode", value);
                            handleZipcodeChange(value);
                        }}
                        errorMessage={getErrorMessage(errorFields, "zipcode")}
                    />
                </div>
                <div className="col-span-1">
                    <SelectTextField
                        id="streetType"
                        name='streetType'
                        label="Tipo"
                        value={form.activeAddress?.street_type || ''}
                        onChange={(e) => changeForm("activeAddress.street_type", e.target.value)}
                        items={streetTypeOptions.value}
                        errorMessage={getErrorMessage(errorFields, "streetType")}
                    />
                </div>
                <div className="col-span-4">
                    <TextEdit
                        id="street"
                        name='street'
                        label="Logradouro"
                        value={form.activeAddress?.street || ''}
                        onChange={(e) => changeForm("activeAddress.street", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "street")}
                    />
                </div>
                <div className="col-span-1">
                    <TextEdit
                        id="streetNumber"
                        name='streetNumber'
                        label="Numero"
                        value={form.activeAddress?.street_number || ''}
                        onChange={(e) => changeForm("activeAddress.street_number", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "streetNumber")}
                    />
                </div>
                <div className="col-span-4">
                    <TextEdit
                        id="complement"
                        label="Complemento"
                        value={form.activeAddress?.complement || ''}
                        onChange={(e) => changeForm("activeAddress.complement", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "complement")}
                    />
                </div>
            </div>

            {/* Quinta linha: Bairro + UF + Cidade */}
            <div className="grid grid-cols-12 gap-4 mt-6">
                <div className="col-span-4">
                    <TextEdit
                        id="district"
                        label="Bairro"
                        value={form.activeAddress?.district || ''}
                        onChange={(e) => changeForm("activeAddress.district", e.target.value.toUpperCase())}
                        errorMessage={getErrorMessage(errorFields, "district")}
                    />
                </div>
                <div className="col-span-2">
                    <SelectTextField
                        id="provinceId"
                        name='provinceId'
                        label="UF"
                        value={form.activeAddress?.province_initials || ''}
                        items={provincesOpt.value}
                        onChange={(e) => {
                            changeForm("activeAddress.province_initials", e.target.value);
                            changeForm("activeAddress.city_id", "");
                            changeProvince(e.target.value);
                        }}
                        errorMessage={getErrorMessage(errorFields, "province")}
                    />
                </div>
                <div className="col-span-4">
                    <SelectTextField
                        id="city"
                        label="Cidade"
                        value={form.activeAddress?.city_id || ''}
                        items={citiesOpt.value}
                        isLoading={isLoadingCities.value}
                        onChange={(e) => changeForm("activeAddress.city_id", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "cityId")}
                        disabled={isLoadingCities.value}
                    />
                </div>
            </div>

        </div >
    );
};
export default IndividualFormBasicData;
